/**
 * User marks: beacons with notes, stored server-side.
 *
 * This is the one part of Opsec that is not static. Notes have to be
 * readable from any device, and that requires shared storage — see
 * eigensite/pulse_notes. Reads are public; writes need a login, so the map
 * stays open while its annotations stay yours.
 *
 * Colour carries the type, and each type has its own silhouette. Colour alone
 * is a poor carrier — it fails against a bright basemap, it fails for the ~8%
 * of men with a colour vision deficiency, and it fails in a screenshot. Shape
 * survives all three, so the two encode the same thing twice.
 *
 * Beacons are drawn as pre-rendered canvas icons rather than stacked
 * scatterplots. A triangle is not expressible as a circle, and one icon per
 * mark is also markedly cheaper than four layers per mark on the animation
 * path.
 */

import { IconLayer, LineLayer, ScatterplotLayer, TextLayer } from "@deck.gl/layers";

import { footprints } from "../map/footprints.js";

import { palette, themeName } from "../theme/palette.js";
import { OVER_EVERYTHING } from "../map/depth.js";

/**
 * The three beacon types.
 *
 * The type names are a starting point, not a taxonomy — the label you give a
 * mark is what actually says what it is. Rename them if they do not match how
 * you use the map.
 */
/**
 * The three beacon types.
 *
 * Shape and meaning are fixed; only the colour follows the theme, so a mark
 * stays the same kind of thing whichever palette is active.
 */
export function markTypes() {
  const c = palette();
  return {
    blue: {
      ...c.markBlue,
      label: "Marker", shape: "pulse",
      hint: "Pulsing dot under a column of light — a standing point of interest",
    },
    amber: {
      ...c.markAmber,
      label: "Alert", shape: "triangle",
      hint: "Triangle — something to be careful of",
    },
    violet: {
      ...c.markViolet,
      label: "Signal", shape: "burst",
      hint: "Radiating circle — something transmitting or watching",
    },
  };
}

/** Kept as a named export: the HUD builds its colour picker from the keys. */
export const MARK_COLOR_KEYS = ["blue", "amber", "violet"];

const DEFAULT_COLOR = "blue";

const keyOf = (mark) => (markTypes()[mark?.color] ? mark.color : DEFAULT_COLOR);
const paletteOf = (mark) => markTypes()[keyOf(mark)];

/** Icon bitmap resolution. Generously sized so beacons stay crisp when zoomed. */
const ICON_PX = 128;

/**
 * The blue beacon gets a taller canvas so its light column has somewhere to go.
 *
 * The anchor sits at the dot rather than at the middle of the bitmap, so the
 * pillar rises from the marked point instead of straddling it. Icons are
 * billboarded, so a flat vertical bar reads as a standing column from any
 * bearing without any actual 3D geometry.
 */
const PILLAR_PX = 208;
const PILLAR_ANCHOR_Y = 150;

/** Rendered size on screen, before the pulse scale. */
const ICON_SIZE = 66;

const rgba = ([r, g, b], a = 1) => `rgba(${r},${g},${b},${a})`;

/**
 * Draw one beacon into a canvas and hand back a data URL.
 *
 * Built lazily on first render, not at module load: this module is imported by
 * the test suite under Node, where there is no `document`.
 */
function drawIcon(key) {
  const { core, glow, shape } = markTypes()[key];
  const tall = shape === "pulse";
  const canvas = document.createElement("canvas");
  canvas.width = ICON_PX;
  canvas.height = tall ? PILLAR_PX : ICON_PX;
  const ctx = canvas.getContext("2d");
  const c = ICON_PX / 2;
  // Vertical centre of the beacon itself, which is lower than the bitmap
  // centre when there is a pillar above it.
  const cy = tall ? PILLAR_ANCHOR_Y : ICON_PX / 2;

  // Every type sits on the same soft glow, which is what makes them read as
  // one family rather than three unrelated symbols.
  const halo = ctx.createRadialGradient(c, cy, 0, c, cy, c);
  halo.addColorStop(0, rgba(glow, 0.42));
  halo.addColorStop(0.55, rgba(glow, 0.12));
  halo.addColorStop(1, rgba(glow, 0));
  ctx.fillStyle = halo;
  ctx.fillRect(0, cy - c, ICON_PX, ICON_PX);

  ctx.lineCap = "round";

  if (shape === "triangle") {
    // Upward triangle with short strokes flying off each edge.
    const r = 30;
    ctx.beginPath();
    for (let i = 0; i < 3; i += 1) {
      const a = -Math.PI / 2 + (i * 2 * Math.PI) / 3;
      const [x, y] = [c + r * Math.cos(a), cy + r * Math.sin(a)];
      if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
    }
    ctx.closePath();
    ctx.fillStyle = rgba(core, 0.22);
    ctx.fill();
    ctx.strokeStyle = rgba(core, 0.95);
    ctx.lineWidth = 5;
    ctx.stroke();

    ctx.strokeStyle = rgba(core, 0.8);
    ctx.lineWidth = 3.5;
    for (let i = 0; i < 6; i += 1) {
      const a = -Math.PI / 2 + (i * Math.PI) / 3 + Math.PI / 6;
      ctx.beginPath();
      ctx.moveTo(c + 40 * Math.cos(a), cy + 40 * Math.sin(a));
      ctx.lineTo(c + 54 * Math.cos(a), cy + 54 * Math.sin(a));
      ctx.stroke();
    }
  } else if (shape === "burst") {
    // Core circle, spokes reaching outward, thin ring closing them off.
    ctx.strokeStyle = rgba(core, 0.75);
    ctx.lineWidth = 3;
    for (let i = 0; i < 12; i += 1) {
      const a = (i * Math.PI) / 6;
      ctx.beginPath();
      ctx.moveTo(c + 20 * Math.cos(a), cy + 20 * Math.sin(a));
      ctx.lineTo(c + 48 * Math.cos(a), cy + 48 * Math.sin(a));
      ctx.stroke();
    }
    ctx.beginPath();
    ctx.arc(c, cy, 54, 0, Math.PI * 2);
    ctx.strokeStyle = rgba(core, 0.85);
    ctx.lineWidth = 2;
    ctx.stroke();

    ctx.beginPath();
    ctx.arc(c, cy, 14, 0, Math.PI * 2);
    ctx.fillStyle = rgba(core, 1);
    ctx.fill();
  } else {
    // A short column of light standing on the mark, drawn before the dot so
    // the dot sits on top of it rather than behind it.
    const top = 34;
    const half = 9;

    const column = ctx.createLinearGradient(0, cy, 0, top);
    column.addColorStop(0, rgba(core, 0.5));
    column.addColorStop(0.45, rgba(core, 0.2));
    column.addColorStop(1, rgba(core, 0));
    ctx.fillStyle = column;
    ctx.fillRect(c - half, top, half * 2, cy - top);

    // Brighter centre line, so the column reads as light rather than as a
    // flat translucent rectangle.
    const spine = ctx.createLinearGradient(0, cy, 0, top);
    spine.addColorStop(0, rgba(core, 0.85));
    spine.addColorStop(1, rgba(core, 0));
    ctx.fillStyle = spine;
    ctx.fillRect(c - 1.5, top, 3, cy - top);

    // Scan bands: the one detail that makes it read as a projection.
    ctx.strokeStyle = rgba(core, 0.3);
    ctx.lineWidth = 1;
    for (let y = cy - 12; y > top + 6; y -= 13) {
      ctx.beginPath();
      ctx.moveTo(c - half, y);
      ctx.lineTo(c + half, y);
      ctx.stroke();
    }

    // Base flare where the column meets the ground.
    const base = ctx.createRadialGradient(c, cy, 0, c, cy, 30);
    base.addColorStop(0, rgba(core, 0.45));
    base.addColorStop(1, rgba(core, 0));
    ctx.fillStyle = base;
    ctx.fillRect(c - 30, cy - 30, 60, 60);

    ctx.beginPath();
    ctx.arc(c, cy, 34, 0, Math.PI * 2);
    ctx.strokeStyle = rgba(core, 0.85);
    ctx.lineWidth = 3;
    ctx.stroke();

    ctx.beginPath();
    ctx.arc(c, cy, 15, 0, Math.PI * 2);
    ctx.fillStyle = rgba(core, 1);
    ctx.fill();
  }

  return canvas.toDataURL();
}

let iconCache = null;
let iconTheme = null;

function icons() {
  // Bitmaps, so the cache is keyed on the theme that produced them.
  const theme = themeName();
  if (iconTheme !== theme) { iconCache = null; iconTheme = theme; }
  if (!iconCache) {
    iconCache = {};
    for (const key of MARK_COLOR_KEYS) {
      const tall = markTypes()[key].shape === "pulse";
      iconCache[key] = {
        url: drawIcon(key),
        width: ICON_PX,
        height: tall ? PILLAR_PX : ICON_PX,
        anchorX: ICON_PX / 2,
        anchorY: tall ? PILLAR_ANCHOR_Y : ICON_PX / 2,
        mask: false,
      };
    }
  }
  return iconCache;
}

/** Is this mark past its expiry? The server hides them too; this covers the
 *  window between a mark expiring and the next reload. */
export function isExpired(mark, at = Date.now()) {
  if (!mark?.expires_at) return false;
  const t = Date.parse(mark.expires_at);
  return Number.isFinite(t) && t <= at;
}

export class MarkStore {
  constructor({ apiBase = "./api" } = {}) {
    this.apiBase = apiBase;
    this.marks = [];
    this.status = "idle";
    this.canWrite = true; // assumed until a write returns 401
    this.enabled = true;
    this.hiddenKinds = new Set();
  }

  get count() {
    return this.visible.length;
  }

  /**
   * Marks that should be on the map right now.
   *
   * Both the count and the render read this, which is the point: the legend
   * filter is applied once, here, rather than at each place that draws.
   */
  get visible() {
    return this.marks.filter((m) =>
      !isExpired(m) && !this.hiddenKinds.has(keyOf(m)));
  }

  // The same three methods Layer offers, so the legend can drive marks and
  // data layers through one interface without knowing which it is holding.
  // MarkStore is not a Layer — it is writable, and owns an editor — so this
  // is duplicated deliberately rather than pulled up into a shared base.
  isHidden(kind) {
    return this.hiddenKinds.has(kind);
  }

  toggleKind(key) {
    if (this.hiddenKinds.delete(key)) return false;
    this.hiddenKinds.add(key);
    return true;
  }

  async load() {
    try {
      const response = await fetch(`${this.apiBase}/marks`);
      if (!response.ok) throw new Error(String(response.status));
      this.marks = await response.json();
      this.status = "ready";
    } catch (error) {
      // The map must work with the notes service down — it is an addition,
      // not a dependency.
      this.status = "unavailable";
      this.marks = [];
      console.warn("marks unavailable:", error.message);
    }
    return this.marks;
  }

  async create({ lng, lat, label, note, color, expires_at }) {
    const response = await fetch(`${this.apiBase}/marks`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ lng, lat, label, note, color, expires_at }),
    });
    if (response.status === 401 || response.status === 403) {
      this.canWrite = false;
      throw new Error("Sign in to add marks");
    }
    if (!response.ok) {
      const body = await response.json().catch(() => null);
      throw new Error(body?.error || `Could not save (${response.status})`);
    }
    const mark = await response.json();
    this.marks.push(mark);
    return mark;
  }

  async update(id, { label, note, color, expires_at }) {
    const response = await fetch(`${this.apiBase}/marks/${encodeURIComponent(id)}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ label, note, color, expires_at }),
    });
    if (!response.ok) throw new Error(`Could not update (${response.status})`);
    const mark = await response.json();
    const index = this.marks.findIndex((m) => m.id === id);
    if (index >= 0) this.marks[index] = mark;
    return mark;
  }

  async remove(id) {
    const response = await fetch(`${this.apiBase}/marks/${encodeURIComponent(id)}`, {
      method: "DELETE",
    });
    if (!response.ok) throw new Error(`Could not delete (${response.status})`);
    this.marks = this.marks.filter((m) => m.id !== id);
  }

  /** Attach a file to an existing mark. */
  async attach(id, file) {
    const body = new FormData();
    body.append("file", file);
    const response = await fetch(`${this.apiBase}/marks/${encodeURIComponent(id)}/files`, {
      method: "POST", body,
    });
    if (response.status === 401 || response.status === 403) {
      throw new Error("Sign in to attach files");
    }
    const payload = await response.json().catch(() => null);
    if (!response.ok) throw new Error(payload?.error || `Upload failed (${response.status})`);

    const mark = this.marks.find((m) => m.id === id);
    if (mark) mark.attachments = [...(mark.attachments ?? []), payload];
    return payload;
  }

  async detach(fileId, markId) {
    const response = await fetch(`${this.apiBase}/files/${encodeURIComponent(fileId)}`, {
      method: "DELETE",
    });
    if (!response.ok) throw new Error(`Could not delete (${response.status})`);
    const mark = this.marks.find((m) => m.id === markId);
    if (mark) mark.attachments = (mark.attachments ?? []).filter((a) => a.id !== fileId);
  }

  fileUrl(fileId) {
    return `${this.apiBase}/files/${encodeURIComponent(fileId)}`;
  }

  describe(mark) {
    const files = mark.attachments ?? [];
    const rows = [
      ["Type", paletteOf(mark).label],
      ["Note", mark.note || "—"],
      ["By", mark.author || "—"],
      ["Added", (mark.created_at || "").replace("T", " ").slice(0, 16)],
    ];
    if (mark.expires_at) {
      rows.push(["Expires", mark.expires_at.replace("T", " ").slice(0, 16)]);
    }
    if (files.length) {
      // Type is always shown so nobody downloads a mystery.
      rows.push(["Files", files.map((f) => `${f.filename} (${f.kind})`).join(", ")]);
    }
    return { title: mark.label || paletteOf(mark).label, rows };
  }

  /**
   * Where a beacon is drawn, in 3D.
   *
   * A mark dropped on a tower was being drawn at ground level, which in a
   * pitched view puts it *inside* the building — the extrusion is solid, so
   * the beacon disappears into it or pokes out of a wall halfway up. Marks
   * placed on a building now sit on its roof.
   *
   * `groundHeight` is supplied by the app, which knows the building
   * footprints; without it every mark stays at street level, which is correct
   * for marks that are genuinely on the street.
   */
  positionOf(mark) {
    const z = this.groundHeight?.(mark) ?? 0;
    return [mark.lng, mark.lat, z];
  }

  /**
   * Beacon layers. `pulse` is a 0..1 phase.
   *
   * Only the blue beacon scales with it — a triangle that throbs reads as a
   * warning going off rather than a mark sitting on a map. The other two are
   * static, and the shared glow keeps them from looking dead next to it.
   */
  render(pulse = 0) {
    const data = this.visible;
    if (!this.enabled || data.length === 0) return [];

    const breathe = 1 + 0.16 * Math.sin(pulse * Math.PI * 2);
    const icon = icons();

    // Marks standing on a roof need to say which roof. Without a tether the
    // beacon reads as floating over the block rather than as belonging to the
    // building under it — and at street level there is nothing to draw, so
    // ground-level marks are unaffected.
    const lifted = data
      .map((m) => ({ mark: m, z: this.groundHeight?.(m) ?? 0 }))
      .filter((d) => d.z > 4);

    return [
      new LineLayer({
        id: "marks-tether",
        data: lifted,
        getSourcePosition: (d) => [d.mark.lng, d.mark.lat, 0],
        getTargetPosition: (d) => [d.mark.lng, d.mark.lat, d.z],
        getColor: (d) => [...paletteOf(d.mark).core, 90],
        updateTriggers: { getColor: themeName() },
        getWidth: 1.5,
        widthUnits: "pixels",
        pickable: false,
        parameters: OVER_EVERYTHING,
      }),
      new IconLayer({
        id: "marks-icon",
        data,
        getIcon: (d) => icon[keyOf(d)],
        getPosition: (d) => this.positionOf(d),
        getSize: (d) => (keyOf(d) === "blue"
          ? ICON_SIZE * (PILLAR_PX / ICON_PX) * breathe
          : ICON_SIZE),
        sizeUnits: "pixels",
        sizeMinPixels: 34,
        sizeMaxPixels: 190,
        billboard: true,
        pickable: true,
        // The icon atlas is redrawn per theme (see icons()); without the theme
        // in the trigger the accessor keeps handing deck the old atlas entry.
        updateTriggers: {
          getSize: pulse,
          getPosition: footprints.stamp,
          getIcon: themeName(),
        },
        parameters: OVER_EVERYTHING,
      }),
      new TextLayer({
        id: "marks-label",
        data: data.filter((m) => m.label),
        getPosition: (d) => this.positionOf(d),
        getText: (d) => d.label,
        getSize: 11,
        getColor: (d) => [...paletteOf(d).core, 235],
        updateTriggers: { getColor: themeName() },
        getPixelOffset: [0, -30],
        fontFamily: "ui-monospace, Menlo, Consolas, monospace",
        characterSet: "auto",
        outlineWidth: 3,
        outlineColor: palette().textHalo,
        fontSettings: { sdf: true },
        pickable: false,
        parameters: OVER_EVERYTHING,
      }),
    ];
  }
}

/**
 * The ring that marks whatever scan is currently looking at.
 *
 * Expands from the centre, fades out, repeats — roughly twice a second, which
 * is fast enough to find with your eye across a room and slow enough not to
 * strobe. Without it the camera arrives somewhere and the thing it came for is
 * one teal dot among dozens.
 */
export function scanPulseLayer(row, phase = 0) {
  if (!row) return [];

  const TEAL = palette().incident;
  // Three rings offset in phase, so one is always mid-expansion.
  return [0, 0.33, 0.66].map((offset, i) => {
    const t = (phase + offset) % 1;
    return new ScatterplotLayer({
      id: `scan-pulse-${i}`,
      data: [row],
      getPosition: (d) => [d.x, d.y],
      getRadius: 12 + t * 130,
      radiusUnits: "meters",
      radiusMinPixels: 4,
      stroked: true,
      filled: false,
      lineWidthUnits: "pixels",
      getLineWidth: 2.5,
      // Fades as it grows, so the ring dies rather than hitting an edge.
      getLineColor: [...TEAL, Math.round(230 * (1 - t) ** 1.5)],
      updateTriggers: { getRadius: t, getLineColor: t },
      parameters: OVER_EVERYTHING,
      pickable: false,
    });
  });
}
